import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { BookOpen, Video, ClipboardCheck, CheckCircle2, Clock, ArrowLeft, ArrowRight, FileText, Layers, Sparkles } from "lucide-react";

const iconMap = { reading: BookOpen, video: Video, quiz: ClipboardCheck, "study-doc": FileText };

// Module ID → Flashcard category mapping
const MODULE_FLASHCARD_CATEGORY = {
  1: "Federal Law",
  2: "SAFE Act",
  3: "Loan Products",
  4: "MLO Activities",
  5: "Ethics & Fraud",
};

export default function ModuleDetailPage() {
  const { moduleId } = useParams();
  const [module, setModule] = useState(null);
  const [progress, setProgress] = useState({});

  useEffect(() => {
    (async () => {
      const [m, p] = await Promise.all([api.get(`/modules/${moduleId}`), api.get("/progress")]);
      setModule(m.data);
      const items = p.data?.items || [];
      const completedMap = {};
      items.filter((i) => i.module_id === parseInt(moduleId)).forEach((i) => (completedMap[i.lesson_id] = true));
      setProgress(completedMap);
    })();
  }, [moduleId]);

  if (!module) return <AppLayout><div className="p-12 text-neutral-500">Loading module…</div></AppLayout>;

  const completedCount = module.lessons.filter((l) => progress[l.id]).length;
  const pct = Math.round((completedCount / module.lessons.length) * 100);

  return (
    <AppLayout>
      <div className="px-6 md:px-10 py-10 max-w-5xl mx-auto" data-testid="module-detail-page">
        <Link to="/modules" className="text-xs text-neutral-500 hover:text-[var(--dw-navy)] inline-flex items-center gap-1 mb-6" data-testid="back-to-modules">
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> All modules
        </Link>

        {/* Header */}
        <div className="grid md:grid-cols-3 gap-8 items-end mb-10 pb-8 border-b border-[var(--dw-navy)]/10">
          <div className="md:col-span-2">
            <div className="overline mb-2">Module {module.id} · {module.weight}% of exam</div>
            <h1 className="font-serif text-5xl text-[var(--dw-navy)] leading-[1.05] mb-3 tracking-tight">{module.title}</h1>
            <p className="text-neutral-600 leading-relaxed">{module.description}</p>
          </div>
          <div>
            <div className="overline mb-2">Your progress</div>
            <div className="font-serif text-4xl text-[var(--dw-navy)] mb-2">{pct}%</div>
            <div className="h-1 bg-[var(--dw-navy)]/10 rounded-sm overflow-hidden mb-2">
              <div className="dw-progress-bar h-full" style={{ width: `${pct}%` }} />
            </div>
            <div className="text-xs text-neutral-500 flex items-center gap-2">
              <Clock className="w-3 h-3" strokeWidth={1.5} /> ~{module.duration_hours} hours · {completedCount}/{module.lessons.length} complete
            </div>
          </div>
        </div>

        {/* Lessons */}
        <div className="space-y-2">
          {module.lessons.map((lesson, idx) => {
            const Icon = iconMap[lesson.type] || BookOpen;
            const done = progress[lesson.id];
            const to = lesson.type === "quiz"
              ? `/modules/${moduleId}/quiz`
              : lesson.type === "sample-test"
              ? `/modules/3/sample-test`
              : `/modules/${moduleId}/lesson/${lesson.id}`;
            return (
              <Link
                to={to}
                key={lesson.id}
                data-testid={`lesson-item-${lesson.id}`}
                className="dw-card group flex items-center gap-5 p-5 hover:border-[var(--dw-gold)]"
              >
                <div className="font-mono-plex text-xs text-neutral-400 w-8 shrink-0">
                  {String(idx + 1).padStart(2, "0")}
                </div>
                <div className="w-9 h-9 flex items-center justify-center border border-[var(--dw-navy)]/12 rounded-sm shrink-0">
                  <Icon className="w-4 h-4 text-[var(--dw-navy)]" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="overline mb-0.5 text-neutral-500">
                    {lesson.type === "reading" ? "Reading" : lesson.type === "video" ? "Video Lecture" : lesson.type === "study-doc" ? "Study Document" : lesson.type === "sample-test" ? "Sample Test" : "Knowledge Check"}
                  </div>
                  <div className="font-serif text-lg text-[var(--dw-navy)] leading-tight truncate">{lesson.title}</div>
                </div>
                <div className="text-xs text-neutral-500 hidden sm:flex items-center gap-1 shrink-0">
                  <Clock className="w-3 h-3" strokeWidth={1.5} /> {lesson.duration_min} min
                </div>
                {done ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" strokeWidth={1.5} />
                ) : (
                  <ArrowRight className="w-4 h-4 text-neutral-400 group-hover:text-[var(--dw-navy)] group-hover:translate-x-1 transition-all shrink-0" strokeWidth={1.5} />
                )}
              </Link>
            );
          })}
        </div>

        {/* Flashcard drill for this module's category */}
        {MODULE_FLASHCARD_CATEGORY[module.id] && (
          <div className="mt-10 pt-8 border-t border-[var(--dw-navy)]/10">
            <div className="overline mb-3">Bonus practice</div>
            <Link
              to={`/flashcards?category=${encodeURIComponent(MODULE_FLASHCARD_CATEGORY[module.id])}`}
              data-testid={`module-flashcards-cta-${module.id}`}
              className="dw-card group relative flex items-center gap-5 p-6 hover:border-[var(--dw-gold)] transition-colors overflow-hidden"
            >
              <div className="w-12 h-12 shrink-0 flex items-center justify-center border border-[var(--dw-gold)]/60 bg-[var(--dw-gold)]/8 rounded-sm">
                <Layers className="w-5 h-5 text-[var(--dw-gold-soft)]" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="overline mb-1 flex items-center gap-1.5 text-[var(--dw-gold-soft)]">
                  <Sparkles className="w-3 h-3" strokeWidth={1.5} /> {MODULE_FLASHCARD_CATEGORY[module.id]} Flashcard Test
                </div>
                <div className="font-serif text-xl text-[var(--dw-navy)] leading-tight mb-1">
                  Drill the {MODULE_FLASHCARD_CATEGORY[module.id]} deck
                </div>
                <div className="text-sm text-neutral-600 leading-relaxed">
                  Adaptive multiple-choice cards drawn from this module's category. Miss one, and it comes back until you nail it.
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-neutral-400 group-hover:text-[var(--dw-navy)] group-hover:translate-x-1 transition-all shrink-0" strokeWidth={1.5} />
            </Link>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
